/**
 * The call script's own state: the verdict, the draft, and the operator's edits.
 *
 * Its own hook rather than more of `useBrain`, because it owns something that
 * one does not: UNSAVED TEXT. Everything else on the card is a view of a stored
 * row and can be thrown away and re-read at any moment. A half-typed correction
 * cannot, and the rest of this file is arranged around that.
 *
 * THE EDITOR IS SEEDED ONCE PER GENERATION, NOT ONCE PER RENDER. The seed watches
 * `generatedAt`, so a card refresh - which the parent does after any change, and
 * which returns the same script - leaves what the operator is typing alone. The
 * usual `useEffect(..., [value])` idiom would clobber it on every poll.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../../../../lib/api";
import {
  generateBrainScript,
  getBrainCallLegal,
  recordBrainCallOutcome,
  refuseBrainCall,
  saveBrainScriptEdits,
  FIX_EDIT_SUFFIX,
  type BrainCallLegalView,
  type BrainCallOutcome,
  type BrainCallScript,
  type BrainCallTouch,
  type BrainFreshnessLike,
  type BrainScriptRefusal,
  type ScriptEdit,
} from "../../../../../lib/siteBrainScript";

export interface ScriptState {
  legal: BrainCallLegalView | null;
  busy: boolean;
  /** The stale-proof refusal, kept on screen until the operator decides. */
  stale: BrainFreshnessLike | null;
  /** Line id -> the text in the box right now. Only ids the operator touched. */
  drafts: Record<string, string>;
  dirty: boolean;
  touch: BrainCallTouch | null;
  setDraft: (id: string, text: string) => void;
  discard: () => void;
  write: (force: boolean) => Promise<void>;
  save: () => Promise<void>;
  refuse: () => Promise<void>;
  setOutcome: (outcome: BrainCallOutcome) => Promise<void>;
}

export function useScript({
  domain,
  script,
  storedTouch,
  onChanged,
  toast,
  t,
}: {
  domain: string;
  script: BrainCallScript | null;
  storedTouch: BrainCallTouch | null;
  /** Ask the card to re-read the row. */
  onChanged: () => void;
  toast: (message: string) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}): ScriptState {
  const [legal, setLegal] = useState<BrainCallLegalView | null>(null);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState<BrainFreshnessLike | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [touch, setTouch] = useState<BrainCallTouch | null>(storedTouch);

  // The generation this editor is seeded from. A NEW script (a different
  // timestamp) clears the boxes, because those edits belonged to sentences that
  // no longer exist; the same script arriving again does not.
  const seeded = useRef<string | null>(null);

  useEffect(() => {
    setTouch(storedTouch);
  }, [storedTouch]);

  useEffect(() => {
    const at = script?.generatedAt ?? null;
    if (at === seeded.current) return;
    seeded.current = at;
    setDrafts({});
  }, [script?.generatedAt]);

  // Asked on mount rather than on the first click. One cheap query, and knowing
  // early is the whole value: the answer changes whether this company is worth
  // the next ten minutes at all.
  useEffect(() => {
    let alive = true;
    void getBrainCallLegal(domain)
      .then((next) => alive && setLegal(next))
      .catch(() => alive && setLegal(null));
    return () => {
      alive = false;
    };
  }, [domain]);

  const setDraft = useCallback((id: string, text: string) => {
    setDrafts((prev) => ({ ...prev, [id]: text }));
  }, []);

  const discard = useCallback(() => setDrafts({}), []);

  /** Only the lines whose text actually differs from what is stored. */
  const edits = useMemo<ScriptEdit[]>(() => {
    if (!script) return [];
    const stored = new Map<string, string>();
    for (const section of script.sections) {
      for (const line of section.lines) {
        stored.set(line.id, line.text);
        // A hook has a second editable string, under a suffixed key. Seeded with
        // "" when the script predates the field, so a remedy typed into an empty
        // box is a real edit rather than an id this filter has never heard of.
        if (section.id === "hooks") stored.set(`${line.id}${FIX_EDIT_SUFFIX}`, line.fix ?? "");
      }
    }
    return Object.entries(drafts)
      .filter(([id, text]) => stored.has(id) && stored.get(id) !== text)
      .map(([id, text]) => ({ id, text }));
  }, [script, drafts]);

  const write = useCallback(
    async (force: boolean) => {
      setBusy(true);
      setStale(null);
      try {
        await generateBrainScript(domain, force);
        // The boxes are cleared by the seed effect above once the new script
        // arrives, so nothing has to be reset here.
        onChanged();
        toast(t("brain.script.written"));
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as BrainScriptRefusal | undefined) : undefined;
        if (body?.error === "stale_evidence" && body.freshness) {
          // Not a toast. This is a decision the operator has to make with the
          // detail in front of them, so it stays on screen until they act.
          setStale(body.freshness);
        } else {
          toast(t(`brain.script.error.${body?.error ?? "pass_failed"}`));
        }
      } finally {
        setBusy(false);
      }
    },
    [domain, onChanged, toast, t],
  );

  const save = useCallback(async () => {
    if (!edits.length) return;
    setBusy(true);
    try {
      await saveBrainScriptEdits(domain, edits);
      setDrafts({});
      onChanged();
      toast(t("brain.script.saved"));
    } catch {
      toast(t("brain.script.error.pass_failed"));
    } finally {
      setBusy(false);
    }
  }, [domain, edits, onChanged, toast, t]);

  const refuse = useCallback(async () => {
    setBusy(true);
    try {
      await refuseBrainCall(domain);
      setLegal(await getBrainCallLegal(domain));
      toast(t("brain.script.refused"));
    } catch {
      toast(t("brain.script.error.pass_failed"));
    } finally {
      setBusy(false);
    }
  }, [domain, toast, t]);

  const setOutcome = useCallback(
    async (outcome: BrainCallOutcome) => {
      setBusy(true);
      try {
        const { touch: next } = await recordBrainCallOutcome(domain, outcome);
        // Kept locally as well as re-read: the outcome chips have to answer the
        // click immediately, and the card's own reload is a round trip away.
        setTouch(next);
        onChanged();
      } catch {
        toast(t("brain.script.error.pass_failed"));
      } finally {
        setBusy(false);
      }
    },
    [domain, onChanged, toast, t],
  );

  return {
    legal,
    busy,
    stale,
    drafts,
    dirty: edits.length > 0,
    touch,
    setDraft,
    discard,
    write,
    save,
    refuse,
    setOutcome,
  };
}
